import { FC, ReactNode, useRef, useMemo } from 'react';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AppPage, AppCard } from '../../components/general';
import CalendarRange, { DefaultRangeSelectType } from '../../components/common/CalendarRange';
import { VoxRoiResult, CostGapWarning } from '../../models/voxRoi';
import styles from './VoxRoi.module.css';

export interface VoxRoiProps {
  status: 'locked' | 'unlocked';
  hasConfig: boolean;
  passphrase: string;
  onPassphraseChange: (value: string) => void;
  onUnlock: () => void;
  unlockError: string | null;
  isConfigLoading: boolean;
  dateRange: Date[] | undefined;
  onDateRangeChange: (range: Date[] | undefined) => void;
  result: VoxRoiResult | null;
  isUsageLoading: boolean;
  costRail?: ReactNode;
}

const usd = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const usd2 = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const pct = (value: number | null) =>
  value === null ? '—' : `${(value * 100).toFixed(1)}%`;

const breadcrumbs = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'VoX ROI Savings' },
];

const rangeOptions = [
  DefaultRangeSelectType.CurrentMonth,
  DefaultRangeSelectType.Past30Days,
  DefaultRangeSelectType.Past2Weeks,
  DefaultRangeSelectType.PastWeek,
];

export const VoxRoi: FC<VoxRoiProps> = ({
  status,
  hasConfig,
  passphrase,
  onPassphraseChange,
  onUnlock,
  unlockError,
  isConfigLoading,
  dateRange,
  onDateRangeChange,
  result,
  isUsageLoading,
  costRail,
}) => {
  const unmappedUsage = result?.warnings.unmappedUsage;
  const loggedUnmappedRef = useRef<unknown>(null);
  if (unmappedUsage && unmappedUsage.length > 0 && loggedUnmappedRef.current !== unmappedUsage) {
    loggedUnmappedRef.current = unmappedUsage;
    console.warn(
      `${unmappedUsage.length} usage row(s) could not be mapped to a catalog product:`,
      unmappedUsage,
    );
    console.table(unmappedUsage);
  }

  if (status === 'locked') {
    return (
      <AppPage title="VoX ROI Savings" breadcrumbs={breadcrumbs}>
        <AppCard title={hasConfig ? 'Unlock your ROI report' : 'Set up your ROI configuration'}>
          <div className={styles.lockFormLayout}>
            <p className={styles.lockFormHint}>
              Your cost configuration is encrypted. Enter your passphrase to {hasConfig ? 'unlock' : 'create'} it.
              Mezzo cannot read your costs and cannot recover a lost passphrase.
            </p>
            <label htmlFor="vox-roi-passphrase" className={styles.lockFormLabel}>Passphrase</label>
            <div className={styles.unlockFieldGroup}>
              <Password
                inputId="vox-roi-passphrase"
                value={passphrase}
                onChange={(event) => onPassphraseChange(event.target.value)}
                feedback={!hasConfig}
                toggleMask
                onKeyDown={(event) => { if (event.key === 'Enter') onUnlock(); }}
              />
              {unlockError && <Message severity="error" text={unlockError} />}
              <Button label={hasConfig ? 'Unlock' : 'Continue setup'} icon="pi pi-lock-open" onClick={onUnlock} className={styles.unlockButton} disabled={isConfigLoading} loading={isConfigLoading} />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="VoX ROI Savings"
      breadcrumbs={breadcrumbs}
      headerEnd={
        <CalendarRange rangeSelectOptions={rangeOptions} value={dateRange} onValueChange={onDateRangeChange} />
      }
    >
      <div className={costRail ? styles.twoPane : undefined}>
        {costRail && <aside className={styles.railPane}>{costRail}</aside>}
        <div className={styles.reportPane}>
          {isUsageLoading && (
            <div className={styles.spinnerContainer}>
              <ProgressSpinner className={styles.spinner} />
            </div>
          )}

          {!isUsageLoading && result && (
            <>
              <MethodologyCallout />
              <ReportContent result={result} />
              <ZeroKnowledgeFooter />
            </>
          )}

          {!isUsageLoading && !result && (
            <AppCard>
              <p className={styles.emptyStateText}>Select a date range to view your ROI report.</p>
            </AppCard>
          )}
        </div>
      </div>
    </AppPage>
  );
};

/* ===== Report Content (unlocked state) ===== */

interface AfterVendorGroup {
  vendor: string;
  products: string[];
  totalCost: number;
}

const MethodologyCallout: FC = () => (
  <div className={styles.methodologyCallout}>
    <i className="pi pi-info-circle" />
    <div>
      <strong>ROI calculation methodology</strong>
      <p className={styles.methodologyText}>
        BEFORE spend estimates the cost of your previous providers, using your configured per-order costs
        multiplied by the average orders per loan. AFTER spend uses actual order counts from Mezzo at
        the cost in effect on each order&rsquo;s date. Savings = BEFORE &minus; AFTER.
      </p>
    </div>
  </div>
);

const ZeroKnowledgeFooter: FC = () => (
  <div className={styles.zkFooter}>
    <i className="pi pi-shield" />
    <span>
      Zero-knowledge &mdash; your cost configuration is encrypted client-side before it reaches Mezzo.
      Mezzo cannot read your costs and cannot recover a lost passphrase.
    </span>
  </div>
);

const ReportContent: FC<{ result: VoxRoiResult }> = ({ result }) => {
  const afterGroups: AfterVendorGroup[] = useMemo(() => {
    const map = new Map<string, AfterVendorGroup>();
    for (const line of result.after.lines) {
      const existing = map.get(line.vendor);
      if (existing) {
        existing.products.push(line.productDisplayName);
        existing.totalCost += line.lineTotal;
      } else {
        map.set(line.vendor, {
          vendor: line.vendor,
          products: [line.productDisplayName],
          totalCost: line.lineTotal,
        });
      }
    }
    return Array.from(map.values());
  }, [result.after.lines]);

  return (
    <>
      {/* ===== Savings Hero Card ===== */}
      <div className={styles.savingsHero}>
        <div className={styles.savingsHeroTitle}>Your Estimated Savings &middot; VoX ROI</div>
        <div className={styles.savingsHeroSubtitle}>
          {result.totalUnitCount.toLocaleString('en-US')} units
          {result.savings.pct !== null && (
            <> &middot; cost per loan {result.savings.pct >= 0 ? 'down' : 'up'} {pct(Math.abs(result.savings.pct))}</>
          )}
        </div>

        <div className={styles.savingsHeroFigures}>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValue}>{usd(result.savings.total)}</span>
            <span className={styles.heroLabel}>Total Saved</span>
          </div>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValueSecondary}>{pct(result.savings.pct)}</span>
            <span className={styles.heroLabel}>Lower Cost Per Loan</span>
          </div>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValueSecondary}>{usd2(result.savings.perLoan)}</span>
            <span className={styles.heroLabel}>Saved Per Loan</span>
          </div>

          {/* Before → After cost-per-loan callout */}
          <div className={styles.costCallout}>
            <div className={styles.costCalloutLabel}>Avg Cost Per Loan</div>
            <div className={styles.costCalloutValues}>
              <span className={styles.costCalloutAmount}>{usd2(result.before.avgCostPerLoan)}</span>
              <span className={styles.costCalloutArrow}>&rarr;</span>
              <span className={styles.costCalloutAmount}>{usd2(result.after.avgCostPerLoan)}</span>
            </div>
          </div>
        </div>

        {/* Supporting pills */}
        <div className={styles.savingsPills}>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Units</div>
            <div className={styles.pillValue}>{result.totalUnitCount.toLocaleString('en-US')}</div>
          </div>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Total Spend Before</div>
            <div className={styles.pillValue}>{usd(result.before.totalSpend)}</div>
          </div>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Total Spend After</div>
            <div className={styles.pillValue}>{usd(result.after.totalSpend)}</div>
          </div>
        </div>
      </div>

      {/* ===== Before / After columns ===== */}
      <div className={styles.columns}>
        {/* BEFORE */}
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <div className={`${styles.sectionIcon} ${styles.sectionIconBefore}`}>
              <i className="pi pi-chart-bar" />
            </div>
            <span className={`${styles.sectionTitle} ${styles.sectionTitleBefore}`}>
              BEFORE
            </span>
            <span className={styles.sectionTitleSuffix}>(Pre-VoX / Pre-Mezzo)</span>
          </div>
          <div className={styles.sectionSubtitle}>Providers used before VoX</div>

          {result.before.providers.length > 0 ? (
            <table className={styles.providerTable}>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Product</th>
                  <th>Cost per Order</th>
                </tr>
              </thead>
              <tbody>
                {result.before.providers.map((provider) => (
                  <tr key={provider.productKey}>
                    <td>{provider.vendor}</td>
                    <td>{provider.productDisplayName}</td>
                    <td>{provider.costPerOrder !== undefined ? usd2(provider.costPerOrder) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noProviderMessage}>
              <Message severity="info" text="No prior providers configured" />
            </div>
          )}

          <p className={styles.routingNote}>
            Order volume intentionally omitted — the savings come from how Mezzo routes and places orders, not from cheaper provider contracts.
          </p>

          {/* Summary box */}
          <div className={styles.summaryBox}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryTitle}>Summary (Before)</span>
              <span className={styles.summaryValue}>{result.before.providerCount} {result.before.providerCount === 1 ? 'Provider' : 'Providers'}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Average cost per order</span>
              <span className={styles.summaryValue}>{usd2(result.before.avgCostPerOrder)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Avg cost per loan (Before)</span>
              <span className={styles.summaryValue}>{usd2(result.before.avgCostPerLoan)}</span>
            </div>
          </div>
        </div>

        {/* AFTER */}
        <div className={`${styles.sectionCard} ${styles.sectionCardAfter}`}>
          <div className={styles.sectionHeader}>
            <div className={`${styles.sectionIcon} ${styles.sectionIconAfter}`}>
              <i className="pi pi-check-circle" />
            </div>
            <span className={`${styles.sectionTitle} ${styles.sectionTitleAfter}`}>
              AFTER
            </span>
            <span className={styles.sectionTitleSuffix}>(With VoX / Mezzo)</span>
          </div>
          <div className={styles.sectionSubtitle}>Providers used with VoX</div>

          {afterGroups.length > 0 ? (
            <table className={`${styles.providerTable} ${styles.providerTableAfter}`}>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Products</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {afterGroups.map((group) => (
                  <tr key={group.vendor}>
                    <td>{group.vendor}</td>
                    <td>{group.products.join(', ')}</td>
                    <td>{usd2(group.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noProviderMessage}>
              <Message severity="info" text="No usage data in selected period" />
            </div>
          )}

          {/* Summary box */}
          <div className={`${styles.summaryBox} ${styles.summaryBoxAfter}`}>
            <div className={styles.summaryRow}>
              <span className={`${styles.summaryTitle} ${styles.summaryTitleAfter}`}>Summary (After)</span>
              <span className={styles.summaryValueGreen}>{afterGroups.length} {afterGroups.length === 1 ? 'Provider' : 'Providers'}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Total actual spend</span>
              <span className={styles.summaryValue}>{usd2(result.after.totalSpend)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Avg cost per loan (After)</span>
              <span className={styles.summaryValueGreen}>{usd2(result.after.avgCostPerLoan)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {result.warnings.unmappedUsage.length > 0 && (
        <div className={styles.warningGroup}>
          <Message
            severity="warn"
            text={`${result.warnings.unmappedUsage.length} usage row(s) could not be mapped to a catalog product.`}
          />
        </div>
      )}
      {result.warnings.productsMissingCost.length > 0 && (
        <div className={styles.warningGroup}>
          {result.warnings.productsMissingCost.map((gap: CostGapWarning) => (
            <Message
              key={gap.productKey}
              severity="warn"
              text={`Missing cost for "${gap.productKey}" on: ${gap.dates.join(', ')}`}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default VoxRoi;
